import { useControllableState } from '@radix-ui/react-use-controllable-state';
import {
  ComponentProps,
  createContext,
  Dispatch,
  forwardRef,
  MutableRefObject,
  PropsWithChildren,
  ReactNode,
  SetStateAction,
  useContext,
  useRef,
} from 'react';
import { useDebouncedCallback } from 'use-debounce';
import {
  CircularProgress,
  List,
  ListItem,
  Popover,
  ScrollAreaRoot,
  ScrollAreaScrollbar,
  ScrollAreaViewport,
  Slot,
  styled,
  TextField,
} from '@streamelements/frontend-ui';
import { KeyboardArrowDownRounded } from '@streamelements/frontend-icons';

TextField.Adornment;

type Props = {
  open?: boolean;
  onOpenChange?(open: boolean): void;
  defaultOpen?: boolean;
  value?: string[];
  defaultValue?: string[];
  onValueChange?(value: string[]): void;
  onChange?(event: Event): void;
  inputValue?: string;
  defaultInputValue?: string;
  onInputValueChange?(next: string): void;
  name?: string;
  multiple?: boolean;
};

export type ComboBoxProps = Props;

const ComboBoxContext = createContext({
  multiple: false,
  name: '' as Props['name'],
  inputRef: { current: null } as MutableRefObject<HTMLInputElement | null>,
  listRef: { current: null } as MutableRefObject<HTMLUListElement | null>,
  selectedOptionRef: { current: null } as MutableRefObject<HTMLLIElement | null>,
  value: [''],
  setValue: (() => void 0) as Dispatch<SetStateAction<string[] | undefined>>,
  defaultValue: [''] as Props['defaultValue'],
  inputValue: '',
  setInputValue: (() => void 0) as Dispatch<SetStateAction<string | undefined>>,
  onChange: (() => void 0) as Props['onChange'],
});

export function ComboBoxRoot({
  open,
  defaultOpen,
  onOpenChange,
  value: valueProp,
  defaultValue: defaultValueProp,
  onValueChange,
  onInputValueChange,
  inputValue: inputValueProp,
  defaultInputValue: defaultInputValueProp,
  children,
  onChange,
  name,
  multiple = false,
}: PropsWithChildren<Props>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const selectedOptionRef = useRef<HTMLLIElement>(null);

  const [isOpen, setIsOpen] = useControllableState({
    prop: open,
    defaultProp: defaultOpen,
    onChange: onOpenChange,
  });

  const [inputValue, setInputValue] = useControllableState({
    prop: inputValueProp,
    defaultProp: defaultInputValueProp,
    onChange: onInputValueChange,
  });

  const [value, setValue] = useControllableState({
    prop: valueProp as string[],
    defaultProp: defaultValueProp as string[],
    onChange: (next) => {
      onValueChange?.(next);
    },
  });

  return (
    <ComboBoxContext.Provider
      value={{
        onChange,
        inputRef,
        listRef,
        selectedOptionRef,
        setValue,
        value: value ?? [''],
        defaultValue: defaultValueProp,
        inputValue: inputValue ?? value?.join('; ') ?? '',
        setInputValue,
        multiple,
        name,
      }}
    >
      <Popover.Root
        open={isOpen}
        onOpenChange={(next) => {
          setIsOpen(next);

          if (!next) {
            setTimeout(() => {
              inputRef.current?.focus();
            }, 0);
          }
        }}
      >
        {children}
        <input type="hidden" name={name} value={value ?? ''} readOnly style={{ display: 'none' }} />
      </Popover.Root>
    </ComboBoxContext.Provider>
  );
}

export const ComboBoxTrigger = forwardRef<
  HTMLInputElement,
  React.ComponentProps<typeof TextField.Single> & { asChild?: boolean }
>(function ComboBoxTrigger({ label, children, asChild, ...props }, ref) {
  const { inputRef, listRef, inputValue, setInputValue } = useContext(ComboBoxContext);
  const textFieldRef = useRef<HTMLDivElement>(null);

  const debouncedOpen = useDebouncedCallback(() => {
    if (textFieldRef.current?.getAttribute('data-state') === 'open') {
      return;
    }
    textFieldRef.current?.click();
  }, 250);

  const Component = asChild ? Slot : TextField.Single;

  return (
    <Popover.Trigger asChild disabled={props.disabled}>
      <Component
        {...(props as Record<string, unknown>)}
        ref={textFieldRef}
        label={label}
        onClick={(e) => {
          if (!props.disabled) return;
          e.preventDefault();
          e.stopPropagation();
        }}
        inputProps={{
          type: 'text',
          autoComplete: 'off',
          ...props.inputProps,
          onClick: (event) => {
            props.inputProps?.onClick?.(event);
            if (props.disabled) return;
            if (!inputValue && !props.inputProps?.readOnly) event.preventDefault();
          },
          onKeyDown: (event) => {
            props.inputProps?.onKeyDown?.(event);

            if (event.defaultPrevented) return;

            switch (event.key) {
              case 'Enter':
                event.preventDefault();
                textFieldRef.current?.click();
                break;
              case 'Tab':
              case 'ArrowDown':
                if (textFieldRef.current?.getAttribute('data-state') !== 'open') {
                  if (event.key !== 'Tab' && props.inputProps?.readOnly) {
                    event.preventDefault();
                    textFieldRef.current?.click();
                    break;
                  }

                  break;
                }
                event.preventDefault();
                (listRef.current?.querySelector('li:first-child') as HTMLElement)?.focus();
                break;
            }
          },
          ref: (element) => {
            if (typeof ref === 'function') ref(element);
            else if (ref) ref.current = element;

            inputRef.current = element;
          },
          css: props.inputProps?.css,
        }}
        css={{ width: '100%', ...props.css }}
        value={inputValue}
        onValueChange={(next: string) => {
          setInputValue(next);
          debouncedOpen();
        }}
      >
        {children}
        {asChild ? null : (
          <ComboBoxAdornment>
            <KeyboardArrowDownRounded />
          </ComboBoxAdornment>
        )}
      </Component>
    </Popover.Trigger>
  );
});

export const ComboBoxAdornment = TextField.Adornment;

export const ComboBoxLoadingIndicator = forwardRef<HTMLDivElement, React.ComponentProps<typeof TextField.Adornment>>(
  function ComboBoxLoadingIndicator(props, ref) {
    return (
      <TextField.Adornment ref={ref} {...props}>
        <CircularProgress.Root size={10} value={25} variant="indeterminate" />
      </TextField.Adornment>
    );
  }
);
ComboBoxLoadingIndicator.displayName = 'TextField.Adornment';

export const ComboBoxContent = forwardRef<HTMLUListElement, React.ComponentProps<typeof List>>(
  function ComboBoxContent(props, ref) {
    const { listRef, selectedOptionRef } = useContext(ComboBoxContext);

    return (
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          sideOffset={8}
          align="start"
          css={{
            padding: 'unset',
            width: 'var(--radix-popover-trigger-width)',
            background: '$uiSecondary',
            boxShadow: '$2',
            border: '1px solid $divider',
            borderRadius: '$base',
            ...props.css,
          }}
          onKeyDown={(event) => {
            let selector = '';

            switch (event.key) {
              case 'ArrowDown':
                event.preventDefault();
                selector = 'li:first-child';
                break;
              case 'ArrowUp':
                event.preventDefault();
                selector = 'li:last-child';
                break;
              default:
                break;
            }

            if (!selector) return;

            if (selectedOptionRef.current) {
              selectedOptionRef.current?.focus();
              return;
            }

            event.preventDefault();
            event.stopPropagation();

            (listRef.current?.querySelector(selector) as HTMLElement)?.focus();
          }}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          <ScrollAreaRoot type="always">
            <ScrollAreaViewport
              css={{ maxHeight: 'var(--radix-popover-content-available-height, 300px)', width: '100%' }}
            >
              <List
                role="listbox"
                ref={(element) => {
                  if (typeof ref === 'function') ref(element);
                  else if (ref) ref.current = element;

                  listRef.current = element;
                }}
                css={{
                  padding: '0.5rem',
                  width: '100%',
                  maxWidth: 'var(--radix-popover-trigger-width)',
                }}
              >
                {props.children}
              </List>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar />
          </ScrollAreaRoot>
        </Popover.Content>
      </Popover.Portal>
    );
  }
);

type ComboBoxOptionProps<C extends ReactNode = ReactNode> = ComponentProps<typeof ListItem> & {
  value: string;
  disabled?: boolean;
  children?: C;
} & (C extends string ? { label?: string } : { label: string });

const StyledListItem = styled(ListItem, {
  '&:focus-visible': {
    outline: '2px solid $uiPrimaryMain',
  },
});

export const ComboBoxOption = forwardRef<HTMLLIElement, ComboBoxOptionProps>(function ComboBoxOption(props, ref) {
  const { value: valueProp, children, label, ...otherProps } = props;
  const { setValue, setInputValue, value, onChange, multiple, selectedOptionRef } = useContext(ComboBoxContext);

  async function selectItem() {
    const event = new Event('change', {
      cancelable: true,
      composed: true,
    });

    onChange?.(event);

    const newValue = multiple
      ? value.includes(valueProp)
        ? value.filter((v) => v !== valueProp)
        : [...value, valueProp]
      : [valueProp];

    setValue(newValue);

    if (event.defaultPrevented) return;

    setInputValue(multiple ? newValue.join('; ') : label || (children as string));
  }

  if (typeof children !== 'string' && !label) {
    throw new Error('ComboBoxOption requires a label prop when children is not a string');
  }

  const isSelected = value.includes(valueProp);

  return (
    <Popover.Close asChild>
      <StyledListItem
        {...otherProps}
        role="option"
        tabIndex={0}
        aria-selected={isSelected}
        aria-label={label}
        state={props.disabled ? 'disabled' : (isSelected ? 'selected' : undefined)}
        ref={(element) => {
          if (isSelected) {
            selectedOptionRef.current = element;
          }

          if (typeof ref === 'function') ref(element);
          else if (ref) ref.current = element;
        }}
        onKeyDown={(event) => {
          switch (event.key) {
            case 'ArrowDown':
              event.preventDefault();
              event.stopPropagation();
              {
                const nextElement = (event.currentTarget.nextElementSibling ||
                  event.currentTarget.parentElement?.firstElementChild) as HTMLElement;
                nextElement?.focus();
              }
              break;
            case 'ArrowUp':
              event.preventDefault();
              event.stopPropagation();
              {
                const previousElement = (event.currentTarget.previousElementSibling ||
                  event.currentTarget.parentElement?.lastElementChild) as HTMLElement;
                previousElement?.focus();
              }
              break;
            case 'Enter':
              selectItem();
              event.currentTarget.click();
              break;
            default:
              break;
          }
        }}
        onPointerUp={selectItem}
      >
        {children || label}
      </StyledListItem>
    </Popover.Close>
  );
});
